import { createApp, type App as VueApp } from 'vue'
import { createPinia } from 'pinia'

import App from '@/app/App.vue'
import router from '@/core/router'
import { getStoredLocale, i18n, resetLoadedLocales, setI18nLocale } from '@/i18n'
import { useLocaleStore } from '@/shared/stores/locale.store'
import { useThemeStore } from '@/shared/stores/theme.store'
import { initThemeFromStorage } from '@/shared/theme'
import { startFitPlaceholderInputs } from '@/shared/utils/fit-placeholder-inputs'

import '@/assets/app.css'
// Must stay after app.css: mobile row-card layout overrides desktop table widths.
import '@/assets/row-card-table-mobile.css'

initThemeFromStorage()

let appInstance: VueApp<Element> | null = null

async function waitForMountTarget(): Promise<Element | null> {
  const existing = document.querySelector('#app')
  if (existing) return existing

  if (document.readyState === 'loading') {
    await new Promise<void>((resolve) => {
      document.addEventListener('DOMContentLoaded', () => resolve(), { once: true })
    })
  }

  return document.querySelector('#app')
}

async function bootstrap() {
  // HMR / simulator iframes can re-run the entry without a host document.
  if (appInstance) return

  const el = await waitForMountTarget()
  if (!el) {
    console.warn('[recruitment-web] mount skipped: #app not found in this document')
    return
  }

  const app = createApp(App)

  const pinia = createPinia()
  app.use(pinia)
  app.use(i18n)
  useLocaleStore(pinia)
  useThemeStore(pinia)
  app.use(router)

  // Load only the active locale (+ fallback) before first render.
  await setI18nLocale(getStoredLocale())

  // Locale load is async — host document may have navigated away (preview iframe).
  if (!document.contains(el)) {
    console.warn('[recruitment-web] mount skipped: #app detached before mount')
    return
  }

  appInstance = app
  app.mount(el)
  startFitPlaceholderInputs()
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    appInstance?.unmount()
    appInstance = null
    resetLoadedLocales()
  })
}

void bootstrap()
